import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { MdEmail, MdSettings } from 'react-icons/md';
import ModalCenter from '@/common/components/modal/ModalCenter';

interface PersonalEmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
}

/**
 * 개인 이메일 인증 안내 모달
 * 개인 이메일 인증을 완료해야 회사 서비스를 이용할 수 있음을 안내
 */
const PersonalEmailVerificationModal: React.FC<PersonalEmailVerificationModalProps> = ({
  isOpen,
  onClose,
  userEmail
}) => {
  const navigate = useNavigate();

  const handleGoToSettings = () => {
    onClose();
    navigate('/settings');
  };

  if (!isOpen) return null;

  return (
    <ModalCenter 
      isOpen={isOpen} 
      onClose={onClose} 
      title="이메일 인증 필요" 
      footer={
        <ModalFooter>
          <CancelButton onClick={onClose}>
            나중에 하기
          </CancelButton>
          <SettingsButton onClick={handleGoToSettings}>
            <MdSettings size={18} />
            계정 설정으로 이동
          </SettingsButton>
        </ModalFooter>
      }
    >
      <ModalContent>
        <IconWrapper>
          <MdEmail size={48} />
        </IconWrapper>
        
        {userEmail && (
          <EmailText>
            <strong>{userEmail}</strong>
          </EmailText>
        )}
        
        <MessageText>
          개인 이메일 인증을 진행해야만<br />
          각 회사의 서비스를 사용할 수 있습니다.
        </MessageText>
        
        <SubText>
          계정 설정에서 이메일 인증을 완료해주세요.
        </SubText>
      </ModalContent>
    </ModalCenter>
  );
};

const ModalContent = styled.div`
  width: 400px;
  padding: 24px;
  text-align: center;
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.warning.secondary};
  border-radius: 50%;
  color: ${semanticColors.text.onWarning.primary};
`;

const EmailText = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0 0 16px 0;
  
  strong {
    color: ${semanticColors.text.brand};
    font-weight: 600;
  }
`;

const MessageText = styled.p`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin: 0 0 12px 0;
  line-height: 1.6;
`;

const SubText = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
  margin: 0;
  line-height: 1.4;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
`;

const CancelButton = styled.button`
  ${typography.paragraph.p2.default}
  flex: 1;
  padding: 12px 16px;
  background: ${semanticColors.background.secondary};
  color: ${semanticColors.text.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.tertiary};
    border-color: ${semanticColors.border.secondary};
  }
`;

const SettingsButton = styled.button`
  ${typography.paragraph.p2.default}
  flex: 1.5;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${semanticColors.background.brand};
  color: ${semanticColors.text.white};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.brandHover || semanticColors.background.brand};
    transform: translateY(-1px);
  }
`;

export default PersonalEmailVerificationModal;

